import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useWatch, useFormContext } from 'react-hook-form';

import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { useDataProvider, Loading, Error } from 'react-admin';

import {
    SelectAllArrayInput,
    SELECT_ALL_VALUE,
    CLEAR_ALL_VALUE,
} from '@src/custom/SelectAllArrayInput';

/**
 * Custom states select component that loads states from the data provider.
 * Uses useWatch and useFormContext for auto-filtering based on available choices.
 */
const StatesSelect = ({ source, ...props }) => {
    const { setValue } = useFormContext();
    const value = useWatch({ name: source });

    const [states, setStates] = useState(null);
    const dataProvider = useDataProvider();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load all states
    useEffect(() => {
        const params = {
            pagination: { page: 1, perPage: 51 },
        };

        dataProvider
            .getList('states', params)
            .then(({ data }) => {
                setStates(data);
                setLoading(false);
                setError(null);
            }).catch((error) => {
                setStates(null);
                setLoading(false);
                setError(error);
            });
    }, [dataProvider]);

    // Update selected states (auto-filter)
    useEffect(() => {
        const safeValue = value || [];
        if (!states) return;
        if (safeValue.length === 0) return;

        // If 'select-all' or 'clear-all' is in value, wait for SelectAllArrayInput to handle it
        if (safeValue.includes(SELECT_ALL_VALUE) || safeValue.includes(CLEAR_ALL_VALUE)) {
            return;
        }

        const stateIds = states.map((s) => s.id);
        const filteredStates = intersection(safeValue, stateIds);

        // Only update if the filtered list is different
        // We compare as strings to handle potential mixed types (e.g. string vs number IDs)
        const safeValueStrings = safeValue.map(String);
        const filteredStrings = filteredStates.map(String);

        if (!isEqual(safeValueStrings, filteredStrings)) {
            setValue(source, filteredStates, { shouldDirty: true, shouldValidate: true });
        }
    }, [states, value, source, setValue]);

    if (loading) return <Loading />;
    if (error) return <Error error={error} />;

    return (
        <SelectAllArrayInput
            {...props}
            source={source}
            choices={states}
        />
    );
};

StatesSelect.propTypes = {
    source: PropTypes.string.isRequired,
};

export default React.memo(StatesSelect);
